// The pack-skin half of C1: which material dresses the chassis, and when the
// materials get generated at all.
//
// DECISION ce1c3046 — chassis for OPERATORS ONLY. The skin is a signal that
// says "you are inside your company's console"; an external visitor holding a
// one-shot link is by definition not, so the portal routes get no chassis, no
// material, and no data-pack. That is why registerMaterials() is NOT called at
// app start the way @vowric/ui's own header suggests: it is called on the
// operator shell's mount (components/OperatorSkin.tsx) and nowhere else in the
// product. A visitor's browser never even generates the tiles.
import { useEffect } from 'preact/hooks';
import { registerMaterials } from '@vowric/ui';
import { getDeploymentVertical } from './api';

/** The verticals that have a skin declared in @vowric/ui's tokens.css. A
 *  vertical with no skin block gets NO data-pack attribute at all — the
 *  unskinned default is a real, blessed state (Actor ships with it), not a
 *  gap to fill with a guess. Keep this in step with the [data-pack="…"]
 *  blocks in tokens/skins.tokens.json. */
// A3 2026-08-19: `actor_assist` joins them. The pack's accent and material are
// blessed (the five A2 design decisions) — rose-magenta, and film at carbon's
// restraint — and until this line existed every CallToWrap screen rendered on
// the neutral chassis with a comment saying it would put the colour on the day
// the skin arrived.
export const SKINNED_VERTICALS = ['rexx', 'workshop', 'actor_assist'] as const;

const PACK_OVERRIDE_KEY = 'vowric_pack_override';

/** Dev override, so the Kit gallery (and a design review on any machine) can
 *  put a skin on without a matching tenant. `?pack=rexx|workshop|none` sets
 *  it for good; the stored value answers thereafter. `none` is distinct from
 *  "unset": it forces the unskinned state over a skinned tenant. */
export function packOverride(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('pack');
    if (fromUrl) window.localStorage.setItem(PACK_OVERRIDE_KEY, fromUrl);
    return window.localStorage.getItem(PACK_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

export function setPackOverride(pack: string | null): void {
  try {
    if (pack === null) window.localStorage.removeItem(PACK_OVERRIDE_KEY);
    else window.localStorage.setItem(PACK_OVERRIDE_KEY, pack);
  } catch {
    // Not persisted; applyPack below still dresses this session.
  }
}

/** The active tenant's vertical → the skin to wear, or null for unskinned. */
export function resolvePack(vertical: string | null): string | null {
  const override = packOverride();
  if (override) return override === 'none' ? null : override;
  if (!vertical) return null;
  return (SKINNED_VERTICALS as readonly string[]).includes(vertical) ? vertical : null;
}

/** Stamp (or clear) data-pack on the root. Returns an undo, so the shell can
 *  hand the attribute back when it unmounts and never leave a portal or a
 *  signed-out screen wearing a skin. */
export function applyPack(pack: string | null): () => void {
  const root = document.documentElement;
  const previous = root.getAttribute('data-pack');
  if (pack) root.setAttribute('data-pack', pack);
  else root.removeAttribute('data-pack');
  return () => {
    if (previous === null) root.removeAttribute('data-pack');
    else root.setAttribute('data-pack', previous);
  };
}

let materialsRegistered = false;

/** Generate the surface materials once per page load. Mount can re-run (route
 *  changes, a breakpoint flip remounting the shell, StrictMode-style double
 *  effects) and each run would otherwise redraw every tile on a canvas —
 *  measurable work for a byte-identical result. */
export function ensureMaterialsRegistered(): void {
  if (materialsRegistered) return;
  materialsRegistered = true;
  try {
    registerMaterials();
  } catch (err) {
    // No 2D canvas (happy-dom under test, and any browser that has canvas
    // disabled). The materials are texture on the chassis and nothing else —
    // --chassis-material simply stays `none` and the chrome renders matte, a
    // complete and legible state by construction (guardrail 4: every
    // generator is near-black-on-black, so their absence removes texture, not
    // information). Nothing downstream reads these variables for meaning.
    // The degradation is designed, but it must not be SILENT (review ruling
    // on C1 web integration erratum E3): one log line so a matte chassis in
    // a real browser is explainable from the console, never a mystery.
    console.info('[skin] materials not registered (no 2D canvas) — chassis renders matte:', err);
  }
}

/** B2 (2026-09-24) — wear the deployment's pack skin for as long as this
 *  component is mounted, then take it off. `Login.tsx` invented this
 *  pattern (its own long comment explains why the chassis rule (operators
 *  only) doesn't apply to a deployment's own front door); `Onboarding.tsx`
 *  and `BetaInvite.tsx` never had it, so both rendered in the default blue
 *  even on a skinned deployment. Shared here so the three can never drift —
 *  each caller keeps its own independent `getDeploymentVertical()` read
 *  (Onboarding already makes one for its own step branching; sharing that
 *  answer would race this hook's mount against that state landing), the
 *  same posture Login already took on its own. */
export function useDeploymentSkin(): void {
  useEffect(() => {
    let cancelled = false;
    let undress: (() => void) | null = null;
    getDeploymentVertical()
      .then((vertical) => {
        if (cancelled) return;
        ensureMaterialsRegistered();
        undress = applyPack(resolvePack(vertical));
      })
      .catch(() => {
        // A failed skin fetch leaves the neutral door, which is honest and
        // still usable. It must never keep anyone out.
      });
    return () => {
      cancelled = true;
      undress?.();
    };
  }, []);
}
